import React, { useEffect, useMemo, useState } from "react";

const AnimatedCircularLoader = ({
  color = "#32008E",
  size = 120,
  strokeWidth = 10,
  initialSeconds = 30,
  active = true,
  fontColor = "#333"
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const [expirationCounter, setExpirationCounter] = useState(initialSeconds);

  useEffect(() => {
    if (!active || expirationCounter <= 0) return;
    const expirationTimeout = setTimeout(() => {
      setExpirationCounter((prev) => prev - 1);
    }, 1000);
    return () => clearTimeout(expirationTimeout);
  }, [expirationCounter, active]);

  useEffect(() => {
    if (active) {
      setExpirationCounter(initialSeconds);
    }
  }, [active, initialSeconds]);

  const [minuteLeft, minuteRight, secondLeft, secondRight] = useMemo(() => {
    const minutes = Math.floor(expirationCounter / 60);
    const seconds = expirationCounter % 60;
    const [m1, m2] = String(minutes).padStart(2, "0").split("");
    const [s1, s2] = String(seconds).padStart(2, "0").split("");
    return [m1, m2, s1, s2];
  }, [expirationCounter]);

  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ display: "block" }}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeOpacity="0.3"
          strokeWidth={strokeWidth}
        />

        {/* Animated arc group */}
        <g>
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            from={`0 ${size / 2} ${size / 2}`}
            to={`360 ${size / 2} ${size / 2}`}
            dur="2s"
            repeatCount="indefinite"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset="0"
          >
            <animate
              attributeName="stroke-dasharray"
              values={`0, ${circumference}; ${
                circumference * 0.25
              }, ${circumference}; 0, ${circumference}`}
              dur="2s"
              repeatCount="indefinite"
              keyTimes="0;0.5;1"
            />
          </circle>
        </g>
      </svg>

      {/* Timer in center */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Roboto",
          fontWeight: "bold",
          fontSize: size * 0.1,
          color: fontColor,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div>
            <span>{minuteLeft}</span>
            <span>{minuteRight}</span>
          </div>
          <span>:</span>
          <div>
            <span>{secondLeft}</span>
            <span>{secondRight}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimatedCircularLoader;
